import React from 'react';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Avatar,
  Menu,
  MenuItem,
  Box,
  Typography,
  ListItemIcon,
  Divider
} from '@mui/material';
import { Logout as LogoutIcon, ContactSupport as ContactSupportIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LanguageSwitcher from '../common/LanguageSwitcher';
import UserProfileSummary from './UserProfileSummary';
import AppVersionLabel from './AppVersionLabel';
import StoreSelectorTrigger from './StoreSelectorTrigger';
import StaffNotificationBell from './StaffNotificationBell';
import ContactSupportIconButton from '../common/ContactSupport';
import { useAuth } from '../../context/AuthContext';
import { useSupportChat } from '../../context/SupportChatContext';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';

const layoutHeaderHeight = 64;
const layoutSurfaceColor = BRAND.surface;

const TopAppBar = ({
  currentStore,
  onDrawerToggle,
  onStoreClick,
  onUserProfileClick,
  storeSelectable = true,
  showBranding = true,
}) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { openChat, isAppAdmin } = useSupportChat();
  const navigate = useNavigate();
  const [userProfileAnchor, setUserProfileAnchor] = React.useState(null);

  const handleUserProfileClick = (event) => {
    setUserProfileAnchor(event.currentTarget);
  };

  const handleUserProfileClose = () => {
    setUserProfileAnchor(null);
  };

  const handleLogout = () => {
    setUserProfileAnchor(null);
    logout();
    navigate('/management/team-member/login', { replace: true });
  };

  return (
    <>
      <AppBar 
        position="sticky" 
        sx={{ 
          top: 0,
          bgcolor: layoutSurfaceColor,
          backgroundImage: 'none',
          color: BRAND.text,
          boxShadow: 'none',
          borderBottom: 'none',
          display: { xs: 'none', lg: 'block' }
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            minHeight: `${layoutHeaderHeight}px !important`,
            px: { lg: 3 },
            bgcolor: layoutSurfaceColor,
            backgroundImage: 'none'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: '1 1 auto', overflow: 'hidden' }}>
            <StoreSelectorTrigger
              currentStore={currentStore}
              onClick={onStoreClick}
              storeSelectable={storeSelectable}
              fallbackLabel="No Store Selected"
              typographyVariant="h6"
              logoUrl={showBranding ? currentStore?.managementLogoUrl : undefined}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <StaffNotificationBell />
            <ContactSupportIconButton />
            <IconButton
              onClick={handleUserProfileClick}
              sx={{ 
                color: BRAND.text,
                bgcolor: brandAlpha('accent', 0.1),
                '&:hover': { bgcolor: brandAlpha('accent', 0.2) }
              }}
            >
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  bgcolor: BRAND.primary,
                  color: BRAND.onPrimary,
                  fontSize: '0.9rem'
                }}
              >
                {user?.name?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* User Profile Menu */}
      <Menu
        anchorEl={userProfileAnchor}
        open={Boolean(userProfileAnchor)}
        onClose={handleUserProfileClose}
        disableScrollLock={true}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            borderRadius: BRAND_RADIUS.button,
            border: `1px solid ${BRAND.line}`,
            boxShadow: `0 12px 32px ${brandAlpha('primary', 0.12)}`
          }
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 0 }}>
          <UserProfileSummary user={user} />
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Box
          sx={{ px: 2, py: 1.5 }}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <Typography
            variant="caption"
            sx={{ mb: 1, fontWeight: 600, color: 'text.secondary', display: 'block' }}
          >
            {t('common.language')}
          </Typography>
          <LanguageSwitcher inMenu sx={{ mb: 0, width: '100%', minWidth: 'unset' }} />
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            handleUserProfileClose();
            if (isAppAdmin) {
              navigate('/management/support');
              return;
            }
            openChat();
          }}
        >
          <ListItemIcon>
            <ContactSupportIcon fontSize="small" sx={{ color: BRAND.accentText }} />
          </ListItemIcon>
          <Typography variant="body2">
            {t(isAppAdmin ? 'navigation.appSupport' : 'common.contactSupport')}
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <Typography variant="body2" color="error">{t('user.logout')}</Typography>
        </MenuItem>
        <Divider />
        <Box sx={{ px: 2, py: 1.5 }}>
          <AppVersionLabel />
        </Box>
      </Menu>
    </>
  );
};

export default TopAppBar;
